import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { compose, find, prop, propEq } from 'ramda'
import { ExtensionValidationRule, OktaClaims, PortalAnnouncement, PortalConfig, ReroutingProduct, Site, User } from '../../app/types'
import { RootState } from '../../app/store'
import { api } from '../../common/api'
import i18n from 'i18next'
import OktaAuth from '@okta/okta-auth-js'

export interface fetchMeArgs {
    oktaAuth: OktaAuth | null
    activeCustomerId: string | null
}

export const fetchMe = createAsyncThunk<User, fetchMeArgs, { rejectValue: null | string }>('authentication/FETCH_ME', async (fetchMeArgs, { rejectWithValue }) => {
    try {
        if (!!fetchMeArgs.oktaAuth) {
            let oktaClaims = null
            try {
                oktaClaims = await fetchMeArgs.oktaAuth.getUser<OktaClaims>()
            } catch (e: any) {
                return rejectWithValue('UNAUTHORIZED')
            }

            oktaClaims.language && i18n.changeLanguage(oktaClaims.language)
            const sessionStorageActiveCustomerId = `activeCustomerId`
            let activeCustomerId = fetchMeArgs.activeCustomerId || sessionStorage.getItem(sessionStorageActiveCustomerId)
            if (activeCustomerId === 'null') {
                activeCustomerId = null
            }
            if (!!activeCustomerId) {
                sessionStorage.setItem(sessionStorageActiveCustomerId, activeCustomerId)
            }
            const { data: user } = await api.get(`/api/me${!!activeCustomerId ? `?activeCustomerId=${activeCustomerId}` : ''}`)
            if (!!user.customerOriginId) {
                sessionStorage.setItem(sessionStorageActiveCustomerId, user.customerOriginId)
            }
            if (user &&
                !!user.customerName &&
                !user.voiceCloudProductId &&
                (!user.trunkingSites || user.trunkingSites.length === 0) &&
                (!user.reroutingProducts || user.reroutingProducts.length === 0)) {
                return rejectWithValue('PRODUCT_NOT_FOUND')
            }
            return {
                ...user,
                language: oktaClaims.language,
                username: oktaClaims.preferred_username,
            }
        } else {
            const sessionStorageActiveCustomerId = `activeCustomerId`
            let activeCustomerId = fetchMeArgs.activeCustomerId || sessionStorage.getItem(sessionStorageActiveCustomerId)
            if (activeCustomerId === 'null') {
                activeCustomerId = null
            }
            if (!!activeCustomerId) {
                sessionStorage.setItem(sessionStorageActiveCustomerId, activeCustomerId)
            }
            const { data: user } = await api.get(`/api/me${!!activeCustomerId ? `?activeCustomerId=${activeCustomerId}` : ''}`)
            if (!!user.customerOriginId) {
                sessionStorage.setItem(sessionStorageActiveCustomerId, user.customerOriginId)
            }
            if (user && !user.voiceCloudProductId && !user.trunkingSites && !user.reroutingProducts) {
                return rejectWithValue('PRODUCT_NOT_FOUND')
            }
            user.language && i18n.changeLanguage(user.language)
            return user
        }
    } catch (err: any) {
        try {
            if (!!err.response) {
                return rejectWithValue(err.response.status.toString())
            } else {
                return rejectWithValue('UNKNOWN_ERROR')
            }
        } catch (e: any) {
            console.error(e)
        }

    }
})

export const fetchGbtfConfig = createAsyncThunk<PortalConfig, string, { state: RootState; rejectValue: null | string }>(
    'authentication/FETCH_CONFIG',
    async (siteProductId, { getState, rejectWithValue }) => {
        try {
            const { data: config } = await api.get('/api/site/' + siteProductId + '/config', {
                headers: {},
            })
            return config
        } catch (err: any) {
            return rejectWithValue(err.response.status)
        }
    },
)

export const selectSite = createAsyncThunk<Site | null, string | null, { state: RootState; rejectValue: null }>('authentication/SELECT_SITE', async (siteProductId, {
    getState,
    dispatch,
    rejectWithValue,
}) => {
    const me = meSelector(getState())
    if (me && me.gbtfSites) {
        const sessionStorageSiteProductIdKey = `${me.username}:selectedSite`
        const siteProductIdFromSessionStorage = sessionStorage.getItem(sessionStorageSiteProductIdKey)
        if (!siteProductId) {
            siteProductId = siteProductIdFromSessionStorage
        }
        if (!!siteProductId) {
            const foundSite = find(propEq('productId', siteProductId), me.gbtfSites) || null
            if (foundSite) {
                sessionStorage.setItem(sessionStorageSiteProductIdKey, foundSite.productId)
                await dispatch(fetchGbtfConfig(foundSite.productId))
            } else {
                if (!!siteProductIdFromSessionStorage) {
                    sessionStorage.removeItem(`${me.username}:selectedSite`)
                }
                if (me.gbtfSites.length > 0) {
                    await dispatch(fetchGbtfConfig(me.gbtfSites[0].productId))
                    return null
                } else {
                    return rejectWithValue(null)
                }
            }
            return foundSite
        } else if (me.gbtfSites.length > 0) {
            await dispatch(fetchGbtfConfig(me.gbtfSites[0].productId))
            return null
        }
        return null
    } else {
        return null
    }
})

export const clearSite = createAsyncThunk<void, undefined, { state: RootState }>('authentication/CLEAR_SITE', async (arg, { getState, dispatch }) => {
    const me = meSelector(getState())
    if (me) {
        sessionStorage.removeItem(`${me.username}:selectedSite`)
    }
})

type State = {
    me: null | User
    loading: boolean
    loadingFailureStatus: undefined | null | string
    selectedSite: null | Site
    previousSelectedSite: null | Site
    config: null | PortalConfig
    refetchStateTrigger: number
    announcementDismissed: boolean
    switchCustomer: boolean
}
const initialState: State = {
    me: null,
    loading: true,
    loadingFailureStatus: null,
    selectedSite: null,
    previousSelectedSite: null,
    config: null,
    refetchStateTrigger: 0,
    announcementDismissed: false,
    switchCustomer: false,
}

const authSlice = createSlice({
    name: 'authentication',
    initialState,
    reducers: {
        clearSelectedSite(state) {
            state.previousSelectedSite = state.selectedSite
            state.selectedSite = null
        },
        announcementDismissed(state) {
            state.announcementDismissed = true
        },
        triggerRefetchSiteState(state) {
            state.refetchStateTrigger = state.refetchStateTrigger + 1
        },
        clear(state) {
            state = initialState
        },
        showCustomerSwitcher(state) {
            state.switchCustomer = true
        },
        hideCustomerSwitcher(state) {
            state.switchCustomer = false
        },
    },
    extraReducers: builder => {
        builder
            .addCase(fetchMe.fulfilled, (state, action) => {
                state.me = action.payload
                state.loadingFailureStatus = null
            })
            .addCase(fetchMe.pending, (state, action) => {
                state.me = null
                state.loading = true
                state.loadingFailureStatus = null
            })
            .addCase(fetchMe.rejected, (state, action) => {
                state.me = null
                state.loading = false
                state.loadingFailureStatus = action.payload
            })
            .addCase(selectSite.pending, (state, action) => {
                state.loadingFailureStatus = null
                state.loading = true
            })
            .addCase(selectSite.fulfilled, (state, action) => {
                state.loading = false
                state.previousSelectedSite = state.selectedSite
                state.selectedSite = action.payload
                state.loadingFailureStatus = null
            })
            .addCase(selectSite.rejected, (state, action) => {
                state.loading = false
                state.previousSelectedSite = state.selectedSite
                state.selectedSite = null
                state.loadingFailureStatus = 'Site not found'
            })
            .addCase(fetchGbtfConfig.fulfilled, (state, action) => {
                state.config = action.payload
            })
            .addCase(clearSite.fulfilled, (state, action) => {
                state.loading = false
                state.selectedSite = null
                state.previousSelectedSite = null
                state.loadingFailureStatus = null
            })
    },
})

export const { clearSelectedSite, triggerRefetchSiteState, announcementDismissed, clear, showCustomerSwitcher, hideCustomerSwitcher } = authSlice.actions

export const authenticationSelector: (state: RootState) => State = prop('authentication')
export const announcementDismissedSelector: (state: RootState) => boolean = compose(prop('announcementDismissed'), authenticationSelector)
export const showCustomerSelector: (state: RootState) => boolean = compose(prop('switchCustomer'), authenticationSelector)
export const meSelector: (state: RootState) => null | User = compose(prop('me'), authenticationSelector)
export const loadingSelector: (state: RootState) => boolean = compose(prop('loading'), authenticationSelector)
export const loadingFailureSelector: (state: RootState) => string | null | undefined = compose(prop('loadingFailureStatus'), authenticationSelector)
export const selectedSiteSelector: (state: RootState) => null | Site = compose(prop('selectedSite'), authenticationSelector)
export const inEnterpriseViewSelector: (state: RootState) => boolean = compose(propEq('selectedSite', null), authenticationSelector)
export const previousSelectedSiteSelector: (state: RootState) => null | Site = compose(prop('previousSelectedSite'), authenticationSelector)
export const refetchStateTriggerSelector: (state: RootState) => number = compose(prop('refetchStateTrigger'), authenticationSelector)

export const sitesSelector: (state: RootState) => null | Site[] = (state: RootState) => {
    const me = meSelector(state)
    return !!me ? me.gbtfSites : []
}
export const noCustomersAccessibleSelector: (state: RootState) => boolean = (state: RootState) => {
    const me = meSelector(state)
    return !!me ? ((me.customerInfos == null || me.customerInfos.length < 1)
        && !me.voiceCloudProductId
        && (me.trunkingSites == null || me.trunkingSites.length < 1)
        && (me.reroutingProducts == null || me.reroutingProducts.length < 1))
        : false
}
export const configSelector: (state: RootState) => null | PortalConfig = compose(prop('config'), authenticationSelector)
export const extensionValidationRulesSelector: (state: RootState) => ExtensionValidationRule[] = (state: RootState) => {
    const config = configSelector(state)
    return config?.extensionValidationRules || []
}
export const allowedUserNcosSelector: (state: RootState) => string = (state: RootState) => {
    const config = configSelector(state)
    return config?.allowedUserNcos || ''
}
export const defaultNcosSelector: (state: RootState) => string = (state: RootState) => {
    const config = configSelector(state)
    return config?.defaultNcos || ''
}
export const nrOfExtensionDigitsSelector: (state: RootState) => number = (state: RootState) => {
    const config = configSelector(state)
    return config?.nrOfExtensionDigits || 4
}
export const announcementSelector: (state: RootState) => PortalAnnouncement | null = (state: RootState) => {
    const config = configSelector(state)
    return config?.announcement || null
}
export const fmuEnabledSelector: (state: RootState) => boolean = (state: RootState) => {
    const config = configSelector(state)
    return config?.fmuEnabled || false
}
export const callRecordingEnabledSelector: (state: RootState) => boolean = (state: RootState) => {
    const config = configSelector(state)
    return config?.callRecordingEnabled || false
}
export const hotDeskEnabledSelector: (state: RootState) => boolean = (state: RootState) => {
    const config = configSelector(state)
    return config?.hotDeskEnabled || false
}

export const pagingEnabledSelector: (state: RootState) => boolean = (state: RootState) => {
    const config = configSelector(state)
    return config?.pagingEnabled || false
}

export const callPickupEnabledSelector: (state: RootState) => boolean = (state: RootState) => {
    const config = configSelector(state)
    return config?.callPickupEnabled || false
}

export const businessReportingSupervisorEnabledSelector: (state: RootState) => boolean = (state: RootState) => {
    const config = configSelector(state)
    return config?.businessReportingSupervisorEnabled || false
}

export const businessReportingEnabledSelector: (state: RootState) => boolean = (state: RootState) => {
    const config = configSelector(state)
    return config?.businessReportingEnabled || false
}

export const multimediaContactCenterEnabledSelector: (state: RootState) => boolean = (state: RootState) => {
    const config = configSelector(state)
    return config?.amccAgentEnabled || config?.amccSupervisorEnabled || false
}

export const multimediaContactCenterSupervisorEnabledSelector: (state: RootState) => boolean = (state: RootState) => {
    const config = configSelector(state)
    return config?.amccSupervisorEnabled || false
}

export const multimediaContactCenterAgentEnabledSelector: (state: RootState) => boolean = (state: RootState) => {
    const config = configSelector(state)
    return config?.amccAgentEnabled || false
}

export const premiumCallCenterEnabledSelector: (state: RootState) => boolean = (state: RootState) => {
    const me = meSelector(state)
    return !!me?.callCenterCodesEnabled
}

export const authorizationCodesEnabledSelector: (state: RootState) => boolean = (state: RootState) => {
    const me = meSelector(state)
    return !!me?.authorizationCodesEnabled
}

export const maxIvrsSelector: (state: RootState) => number = (state: RootState) => {
    const config = configSelector(state)
    return config?.maxNrOfIvrs || 0
}

export const maxCallQueueSelector: (state: RootState) => number = (state: RootState) => {
    const config = configSelector(state)
    return config?.maxNrOfCallQueues || 0
}


/**
 * Returns the flat union of all VpPrivileges for the current user.
 * Empty array when the user has no VpUser record (not yet provisioned).
 */
export const vpPrivilegesSelector = (state: RootState): string[] => {
    const me = meSelector(state)
    return me?.vpPrivileges ?? []
}

/**
 * Returns the per-site VpPrivileges map for the current user.
 */
export const sitePrivilegesSelector = (state: RootState): Record<string, string[]> => {
    const me = meSelector(state)
    return me?.sitePrivileges ?? {}
}


export const customerOriginIdSelector: (state: RootState) => string | null = (state: RootState) => {
    const me = meSelector(state)
    return me?.customerOriginId || null
}

export const reroutingProductSelector: (state: RootState) => ReroutingProduct | null = (state: RootState) => {
    const me = meSelector(state)
    return me?.reroutingProducts && me?.reroutingProducts.length > 0 ? me?.reroutingProducts[0] : null
}

export const customerNeedsTobeSelectedSelector: (state: RootState) => boolean = (state: RootState) => {
    const me = meSelector(state)
    const auth = authenticationSelector(state)
    return (!me?.customerOriginId && !!me?.customerInfos && me.customerInfos.length > 0) || auth.switchCustomer
}

export const isCallCenterCodeEnabledSelector = (state: RootState) => {
    const me = meSelector(state)
    return !!me?.callCenterCodesEnabled
}

export const disallowEnterpriseExtensionDialingSelector = (state: RootState) => {
    const config = configSelector(state)
    return config?.disallowEnterpriseExtensionDialing
}

export const premiumCallCenterAllowedForLicenseSelector = (state: RootState) => {
    const config = configSelector(state)
    return config?.premiumCallCenterAllowedForLicense
}

export const reducer = authSlice.reducer
